import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TouchableWithoutFeedback,
  TextInput,
  Modal,
  Platform,
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Linking,
  useWindowDimensions,
  Pressable, // <-- add
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import axios from 'axios';
import { Ionicons } from '@expo/vector-icons';
import BaseWeb from '../components/baseWeb';
import { useLogScreen } from '../../useLogScreen';

function formatCurrencyBR(value = 0) {
  const n = Math.round(value || 0);
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    const v = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `R$ ${v}`;
  }
}
function parseBRLCurrencyToNumber(text = '') {
  const digits = String(text).replace(/\D/g, '');
  return Number(digits || 0);
}

export default function ResultadoDILWeb() {
  useLogScreen('DilResultadoWeb');
  const navigation = useNavigation();
  const route = useRoute();
  const resultado = route?.params?.resultado || null;

  useEffect(() => {
    if (Platform.OS === 'web') {
      setTimeout(() => {
        document.title = 'imoGo | Resultado Precificador';
      }, 100);
    }
  }, []);

  // Fecha o modal ao pressionar ESC (web)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && valorEditModalVisible) {
        fecharModalEdicao();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [valorEditModalVisible]);

  const estimativa = resultado?.resultado;
  const [estimativaLocal, setEstimativaLocal] = useState(estimativa || null);

  const valorEstimadoOriginalRef = useRef(
    typeof estimativa?.valor_estimado === 'number' ? estimativa.valor_estimado : 0
  );

  const [valorEditModalVisible, setValorEditModalVisible] = useState(false);
  const [valorEditNumero, setValorEditNumero] = useState(
    typeof estimativa?.valor_estimado === 'number' ? estimativa.valor_estimado : 0
  );
  const [valorEditTexto, setValorEditTexto] = useState(
    formatCurrencyBR(typeof estimativa?.valor_estimado === 'number' ? estimativa.valor_estimado : 0)
  );

  const [gerandoLaudo, setGerandoLaudo] = useState(false);

  useEffect(() => {
    if (typeof estimativa?.valor_estimado === 'number') {
      setEstimativaLocal(estimativa);
      setValorEditNumero(estimativa.valor_estimado);
      setValorEditTexto(formatCurrencyBR(estimativa.valor_estimado));
      valorEstimadoOriginalRef.current = estimativa.valor_estimado;
    }
  }, [estimativa]);

  const precoFormatado =
    estimativaLocal?.formatado_ptbr?.valor_estimado ??
    (typeof estimativaLocal?.valor_estimado === 'number'
      ? formatCurrencyBR(estimativaLocal.valor_estimado)
      : '—');

  const onChangeValorText = (text) => {
    const number = parseBRLCurrencyToNumber(text);
    setValorEditNumero(number);
    setValorEditTexto(formatCurrencyBR(number));
  };
  const abrirModalEdicao = () => setValorEditModalVisible(true);
  const fecharModalEdicao = () => setValorEditModalVisible(false);
  const confirmarEdicao = () => {
    if (!Number.isFinite(valorEditNumero) || valorEditNumero <= 0) {
      alert('Valor inválido. Informe um valor maior que zero.');
      return;
    }
    setEstimativaLocal((prev) => {
      const novo = {
        ...(prev || {}),
        valor_estimado: valorEditNumero,
        formatado_ptbr: {
          ...(prev?.formatado_ptbr || {}),
          valor_estimado: formatCurrencyBR(valorEditNumero),
        },
      };
      return novo;
    });
    setValorEditModalVisible(false);
  };

  const adjustBy = (delta) => {
    const v = Math.max(0, (valorEditNumero || 0) + delta);
    setValorEditNumero(v);
    setValorEditTexto(formatCurrencyBR(v));
  };
  const adjustByPercent = (pct) => {
    const base = valorEditNumero || 0;
    const v = Math.max(0, Math.round(base * (1 + pct)));
    setValorEditNumero(v);
    setValorEditTexto(formatCurrencyBR(v));
  };

  const baseValor = estimativaLocal?.valor_estimado || 0;
  const deltaValor = (valorEditNumero || 0) - baseValor;
  const deltaPct = baseValor > 0 ? deltaValor / baseValor : 0;
  const formatSignedCurrency = (n) => {
    const sign = n > 0 ? '+' : n < 0 ? '-' : '';
    return `${sign}${formatCurrencyBR(Math.abs(n))}`;
  };
  const formatSignedPercent = (ratio) => {
    const pct = Math.round((ratio || 0) * 100);
    const sign = pct > 0 ? '+' : pct < 0 ? '-' : '';
    return `${sign}${Math.abs(pct)}%`;
  };

  const isErroAmostra = resultado?.ok === false;

  const { width: winW, height: winH } = useWindowDimensions();
  const isLandscape = winW > winH;
  const isTablet = Math.min(winW, winH) >= 768;
  const modalCardDynamic = {
    width: Math.min(isTablet ? 600 : winW * 0.92, 640),
    maxHeight: winH * (isLandscape ? 0.9 : 0.85),
    padding: Math.max(12, Math.min(16, winW * 0.04)),
  };

  const handleNovaConsulta = () => {
    if (navigation.canGoBack()) navigation.navigate('DilWeb');
    else navigation.goBack();
  };

  const handleBaixarLaudo = async () => {
    try {
      setGerandoLaudo(true);

      const imovel =
        resultado?.imovel ||
        resultado?.dados_imovel ||
        resultado?.entrada ||
        resultado?.input ||
        {};

      const valorM2Number =
        typeof estimativa?.valor_m2_ponderado === 'number'
          ? estimativa.valor_m2_ponderado
          : typeof estimativa?.valor_m2_medio === 'number'
          ? estimativa.valor_m2_medio
          : undefined;

      const payload = {
        bairro: imovel.bairro ?? '',
        cidade: imovel.cidade ?? '',
        endereco: imovel.endereco ?? imovel.logradouro ?? '',
        estado_conservacao: imovel.estado_conservacao ?? imovel.estado ?? '',
        tipo: imovel.tipo ?? imovel.tipo_imovel ?? '',
        quartos: Number(imovel.quartos ?? imovel.qtd_quartos ?? 0),
        suites: Number(imovel.suites ?? imovel.qtd_suites ?? 0),
        vagas: Number(imovel.vagas ?? imovel.qtd_vagas ?? 0),
        metragem: String(imovel.metragem_param ?? imovel.area ?? imovel.metros_quadrados ?? ''),
        valor_m2_ponderado:
          typeof valorM2Number === 'number'
            ? formatCurrencyBR(valorM2Number)
            : String(estimativa?.valor_m2_ponderado ?? ''),
        valor_imovel: formatCurrencyBR(estimativaLocal?.valor_estimado || 0),       // final (editado)
        valor_estimado: formatCurrencyBR(valorEstimadoOriginalRef.current || 0),    // original
      };

      const { data } = await axios.post(
        'https://gateway-laudo.vercel.app/api/laudo/gerar',
        payload,
        { headers: { 'Content-Type': 'application/json' }, timeout: 60000 }
      );

      if (data?.ok && data?.docx_response) {
        const pdfUrl = data.docx_response.pdf_url;
        const pptxUrl = data.docx_response.pptx_url;
        const url = pdfUrl || pptxUrl;

        setGerandoLaudo(false);

        if (url) {
          if (Platform.OS === 'web') {
            try {
              window.open(url, '_blank', 'noopener,noreferrer');
              return;
            } catch {}
          }
          const supported = await Linking.canOpenURL(url);
          if (supported) {
            await Linking.openURL(url);
            return;
          }
          alert('Laudo gerado, mas não foi possível abrir o link.');
        } else {
          alert('Laudo gerado, porém a URL do documento não foi encontrada.');
        }
      } else {
        alert(data?.mensagem || 'Não foi possível gerar o laudo.');
      }
    } catch (err) {
      alert(err?.response?.data?.mensagem || err?.message || 'Erro ao gerar o laudo.');
    } finally {
      setGerandoLaudo(false);
    }
  };

  return (
    <BaseWeb>
      {(userInfo) => (
        <ScrollView contentContainerStyle={styles.content}>
          <View style={styles.container}>
            <View style={styles.headerRow}>
              <Text style={styles.title}>Precificador</Text>
              <TouchableOpacity onPress={handleNovaConsulta} style={styles.secondaryBtn}>
                <Text style={styles.secondaryBtnText}>Nova consulta</Text>
              </TouchableOpacity>
            </View>

            {isErroAmostra ? (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>Não foi possível gerar o laudo</Text>
                <Text style={styles.muted}>
                  {resultado?.mensagem ||
                    'A quantidade de amostras para a região informada é insuficiente para criar um laudo.'}
                </Text>
              </View>
            ) : (
              <>
                <View style={styles.centerBox}>
                  <Image
                    source={require('../../assets/img/money.png')}
                    style={styles.image}
                    resizeMode="contain"
                  />
                  <Text style={styles.subtitleCenter}>
                    Obrigado por realizar a sua solicitação do imóvel conosco!
                  </Text>
                  <Text style={styles.mutedCenter}>
                    Com base nos valores que os imóveis similares foram negociados recentemente, nossa
                    IA precificou o seu imóvel no valor de:
                  </Text>
                  <Text style={styles.priceText}>{precoFormatado}</Text>
                </View>

                <View style={styles.actionsCol}>
                  <TouchableOpacity style={styles.optionButton} onPress={abrirModalEdicao}>
                    <Ionicons name="create-outline" size={18} color="#730d83" style={{ marginRight: 10 }} />
                    <Text style={styles.optionButtonText}>Ajustar Valor</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.optionButton} onPress={handleBaixarLaudo}>
                    <Ionicons name="download-outline" size={18} color="#730d83" style={{ marginRight: 10 }} />
                    <Text style={styles.optionButtonText}>Baixar Laudo (PDF)</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>

          {/* Modal de edição do valor */}
          <Modal
            transparent
            visible={valorEditModalVisible}
            animationType="fade"
            onRequestClose={fecharModalEdicao}
            onDismiss={fecharModalEdicao}
          >
            <View style={styles.modalRoot} pointerEvents="box-none">
              {/* Clicar fora do card fecha o modal */}
              <Pressable style={styles.modalOverlay} onPress={fecharModalEdicao} />

              <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                style={styles.modalAvoider}
                pointerEvents="box-none"
              >
                <View style={[styles.modalCard, modalCardDynamic]}>
                  <TouchableOpacity
                    style={styles.modalCloseBtn}
                    onPress={fecharModalEdicao}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    accessibilityRole="button"
                    accessibilityLabel="Fechar"
                  >
                    <Ionicons name="close" size={22} color="#730D83" />
                  </TouchableOpacity>

                  <ScrollView
                    contentContainerStyle={styles.modalContent}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                  >
                    <Text style={styles.modalTitle}>Ajustar valor do laudo</Text>

                    <Text style={styles.modalLabelMuted}>Valor atual</Text>
                    <Text style={styles.modalCurrentValue}>{precoFormatado}</Text>

                    <Text style={styles.modalLabel}>Novo valor</Text>
                    <View style={styles.inputRow}>
                      <TextInput
                        value={valorEditTexto}
                        onChangeText={onChangeValorText}
                        keyboardType="number-pad"
                        placeholder="R$ 0"
                        style={styles.currencyInput}
                      />
                      <View style={styles.stepper}>
                        <TouchableOpacity style={styles.stepperBtn} onPress={() => adjustBy(-1000)}>
                          <Ionicons name="remove" size={18} color="#730D83" />
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.stepperBtn} onPress={() => adjustBy(+1000)}>
                          <Ionicons name="add" size={18} color="#730D83" />
                        </TouchableOpacity>
                      </View>
                    </View>

                    <View style={styles.deltaRow}>
                      <Text
                        style={[
                          styles.deltaText,
                          deltaValor > 0 ? styles.deltaUp : deltaValor < 0 ? styles.deltaDown : null,
                        ]}
                      >
                        Variação: {formatSignedCurrency(deltaValor)} ({formatSignedPercent(deltaPct)})
                      </Text>
                    </View>

                    <View style={styles.quickRow}>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustBy(-10000)}>
                        <Text style={styles.chipBtnText}>-10k</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustBy(-5000)}>
                        <Text style={styles.chipBtnText}>-5k</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustBy(+5000)}>
                        <Text style={styles.chipBtnText}>+5k</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustBy(+10000)}>
                        <Text style={styles.chipBtnText}>+10k</Text>
                      </TouchableOpacity>
                    </View>

                    <Text style={styles.modalLabel}>Variação percentual</Text>
                    <View style={styles.quickRow}>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustByPercent(-0.05)}>
                        <Text style={styles.chipBtnText}>-5%</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustByPercent(-0.01)}>
                        <Text style={styles.chipBtnText}>-1%</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustByPercent(+0.01)}>
                        <Text style={styles.chipBtnText}>+1%</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.chipBtn} onPress={() => adjustByPercent(+0.05)}>
                        <Text style={styles.chipBtnText}>+5%</Text>
                      </TouchableOpacity>
                    </View>

                    <View style={styles.modalButtons}>
                      <TouchableOpacity style={[styles.modalButton, styles.modalCancel]} onPress={fecharModalEdicao}>
                        <Text style={styles.modalButtonTextCancel}>Cancelar</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.modalButton,
                          styles.modalConfirm,
                          (estimativaLocal?.valor_estimado || 0) === (valorEditNumero || 0) && styles.modalConfirmDisabled,
                        ]}
                        onPress={confirmarEdicao}
                        disabled={(estimativaLocal?.valor_estimado || 0) === (valorEditNumero || 0)}
                      >
                        <Text style={styles.modalButtonTextConfirm}>Salvar</Text>
                      </TouchableOpacity>
                    </View>
                  </ScrollView>
                </View>
              </KeyboardAvoidingView>
            </View>
          </Modal>

          {/* Loading ao gerar laudo */}
          <Modal transparent visible={gerandoLaudo} animationType="fade" onRequestClose={() => {}}>
            <View style={styles.loadingCenter} pointerEvents="box-none">
              <View style={styles.loadingOverlay} />
              <View style={styles.loadingCard}>
                <ActivityIndicator size="large" color="#730D83" />
                <Text style={styles.loadingText}>Criando laudo... Isso pode levar alguns segundos.</Text>
              </View>
            </View>
          </Modal>
        </ScrollView>
      )}
    </BaseWeb>
  );
}

const styles = {
  content: {
    paddingVertical: 20,
    paddingHorizontal: 15,
    alignItems: 'center',
  },
  container: {
    width: '100%',
    maxWidth: 900,
  },
  headerRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1F2024',
  },
  subtitleCenter: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2024',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  mutedCenter: {
    fontSize: 14,
    color: '#2F3036',
    textAlign: 'center',
    marginBottom: 12,
  },
  card: {
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#E3E3E3',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  centerBox: {
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#E3E3E3',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  image: {
    width: 280,
    height: 180,
    marginBottom: 8,
  },
  priceText: {
    color: '#730D83',
    fontSize: 28,
    fontWeight: '800',
    marginTop: 6,
  },
  actionsCol: {
    width: '100%',
    gap: 10,
    marginTop: 10,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  optionButtonText: {
    color: '#1F2024',
    fontSize: 14,
    fontWeight: '700',
  },
  secondaryBtn: {
    backgroundColor: '#EFEFEF',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 24,
  },
  secondaryBtnText: {
    color: '#1F2024',
    fontSize: 14,
    fontWeight: '600',
  },

  // Modal
  modalRoot: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1010
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 999,           // garante overlay acima do conteúdo
  },
  modalAvoider: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    zIndex: 1000,          // conteúdo acima do overlay
  },
  modalCard: {
    width: '88%',
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    padding: 16,
    elevation: 6,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    position: 'relative',
    zIndex: 1001,          // card acima do overlay
  },
  modalCloseBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f5f5f5',
    borderWidth: 1,
    borderColor: '#D3D3D3',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 3,
    zIndex: 1002,
  },
  modalContent: {
    paddingTop: 4,
    paddingBottom: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1F2024',
    marginBottom: 10,
    textAlign: 'center',
  },
  modalLabelMuted: {
    fontSize: 12,
    color: '#7A7A7A',
    marginTop: 6,
  },
  modalCurrentValue: {
    fontSize: 22,
    fontWeight: '800',
    color: '#730D83',
    marginBottom: 12,
  },
  modalLabel: {
    fontSize: 14,
    color: '#1F2024',
    marginBottom: 6,
    marginTop: 4,
    fontWeight: '600',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  currencyInput: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: Platform.select({ ios: 12, android: 8, default: 10 }),
    fontSize: 18,
    color: '#1F2024',
    textAlign: 'left',
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  stepperBtn: {
    backgroundColor: '#EEE0F0',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D3D3D3',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    gap: 10,
  },
  modalButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  modalCancel: {
    backgroundColor: '#f5f5f5',
    borderColor: '#D3D3D3',
  },
  modalConfirm: {
    backgroundColor: '#730D83',
    borderColor: '#730D83',
  },
  modalConfirmDisabled: {
    opacity: 0.6,
  },
  modalButtonTextCancel: {
    color: '#1F2024',
    fontWeight: '600',
  },
  modalButtonTextConfirm: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  quickRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 10,
    marginBottom: 6,
  },
  chipBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: '#EEE0F0',
    borderWidth: 1,
    borderColor: '#D3D3D3',
  },
  chipBtnText: {
    color: '#730D83',
    fontWeight: '700',
    fontSize: 13,
  },
  deltaRow: {
    marginTop: 8,
    marginBottom: 4,
  },
  deltaText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#7A7A7A',
  },
  deltaUp: { color: '#2E7D32' },
  deltaDown: { color: '#C62828' },

  // Loading gerar laudo
  loadingOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2147483646,
  },
  loadingCenter: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2147483647,
  },
  loadingCard: {
    width: '80%',
    maxWidth: 420,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 6,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    zIndex: 2147483647,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#1F2024',
    textAlign: 'center',
  },
};